import { adoptQueued, cardAccount, cardTold, cardUntold, getQueue, isLocalOnly, jobOutcomes, onQueueChange, setCardInUse, type CardTap, type QueuedMutation } from './api'
import { getMe } from './me'
import { visibleTime } from './business-settings'
import { layWaiting } from './waiting-changes'
import { orderDay } from './day-order'
import { houseOrder } from './jobs'
import { severalJobsOn } from './job-names'
import { kindLabel } from './trade'
import { roundConfig, shownTime } from './round-config'
import { splitAccessWarning, todayISO } from './ui'
import type { TodayClean, TodayPayload } from './types'

// The lock screen card in Roundly's iPhone app (docs/plans/lock-screen/REAL-CARD.md; Liam, 21 Oct 2026: "Go build the
// real card"). The app draws the card and writes a tap down; it never sends anything to Roundly. This page:
//   - hands the app the person's own jobs today as Today shows them (card.set), whenever they change on this phone;
//   - takes the app's taps into its own queue (api.ts adoptQueued), which sends them like a Today change made with no
//     signal, then tells the app it may forget them (card.taken);
//   - tells the app to take the card off when the person signs out or switches round (card.clear).
// Only in an app that says it has the card (app.info card), only for someone with jobs of their own.

interface RoundlyApp { call(method: string, params?: unknown): Promise<unknown> }

function roundlyApp(): RoundlyApp | null {
  if (typeof window === 'undefined') return null
  const a = (window as unknown as { RoundlyIOS?: RoundlyApp }).RoundlyIOS
  return a && typeof a.call === 'function' ? a : null
}

async function call(method: string, params?: unknown): Promise<unknown> {
  const a = roundlyApp()
  if (!a) return null
  try { return await a.call(method, params) } catch { return null }
}

let supported: boolean | null = null
async function cardSupported(): Promise<boolean> {
  if (supported !== null) return supported
  if (!roundlyApp()) return (supported = false)
  const info = (await call('app.info')) as { card?: unknown } | null
  // A failed ask is not kept: the next one asks again.
  if (!info) return false
  return (supported = info.card === true)
}

// ---- who, and the epoch --------------------------------------------------------------------------------------------

// Whose card this is: the account the queue is for, with jobs of their own.
function account(): { email: string; businessId: number; worker: string; key: string } | null {
  const me = getMe()
  const acc = cardAccount()
  const worker = me?.workerName?.trim()
  if (!acc || !worker || (me?.role !== 'owner' && me?.role !== 'worker')) return null
  return { ...acc, worker, key: `${acc.email}|${acc.businessId}|${worker}` }
}

// A number that only rises, kept on the phone (B7): the app takes nothing older than the last it took, so a read
// started before a sign-out or a round switch never puts the old card back. Not the queue's own generation, which
// starts again at 0 on every page load.
const EPOCH_KEY = 'roundly:card-epoch'
let epoch = 0
function nextEpoch(): number {
  let saved = 0
  try { saved = Number(localStorage.getItem(EPOCH_KEY)) || 0 } catch { /* storage off */ }
  epoch = Math.max(saved + 1, Date.now(), epoch + 1)
  try { localStorage.setItem(EPOCH_KEY, String(epoch)) } catch { /* the page's own number still rises */ }
  return epoch
}

// ---- the day, as last read (C4) -------------------------------------------------------------------------------------

// The visit's own fields: a saved answer changes these, by version. Everything else (the customer's name, address,
// postcode, the job's name) comes only from reads, the newest read winning.
const VISIT_FIELDS = ['version', 'state', 'started_at', 'finished_at', 'blocked_reason', 'blocked_at', 'blocked_note', 'date',
  'time_slot', 'confirmed', 'source', 'payment_status', 'payment_method', 'amount_pence', 'kind', 'duration_minutes'] as const

// `gone`: jobs a saved answer took off the day, with that answer's version, so an older read cannot put them back
// (GPT-6.1's review).
// `held`: jobs given to nobody by a save on this phone, with that save's version: off the card until a read at least
// that new says they are still this person's (Astra's final review, round 4).
interface Day { account: string; date: string; order: number[]; rows: Map<number, TodayClean>; gone: Map<number, number>; held: Map<number, number> }
let day: Day | null = null
const DAY_PREFIX = 'roundly:card-day:v1:'

function loadDay(acc: string, date: string): Day | null {
  try {
    const raw = JSON.parse(localStorage.getItem(DAY_PREFIX + acc) ?? 'null') as { date?: string; rows?: TodayClean[] } | null
    if (!raw || raw.date !== date || !Array.isArray(raw.rows)) return null
    return { account: acc, date, order: raw.rows.map((r) => r.id), rows: new Map(raw.rows.map((r) => [r.id, r])), gone: new Map(), held: new Map() }
  } catch { return null }
}

function saveDay(d: Day): void {
  try { localStorage.setItem(DAY_PREFIX + d.account, JSON.stringify({ date: d.date, rows: d.order.map((id) => d.rows.get(id)).filter(Boolean) })) } catch { /* kept in memory */ }
}

// The account whose card was taken off because Roundly refused its read (signed out, removed, paused): nothing of it is
// handed over again, not even its saved day, until a read for it succeeds (Astra's final review).
let refusedFor: string | null = null

function currentDay(): Day | null {
  const acc = account()
  if (!acc || acc.key === refusedFor) return null
  const date = todayISO()
  if (day && day.account === acc.key && day.date === date) return day
  day = loadDay(acc.key, date)
  return day
}

// A saved answer (or a refusal's own row) for one visit: its visit fields when newer; off the day when it moved off
// today or was given to someone else (C4, C5).
function rowSaved(e: Event): void {
  const d = currentDay()
  const detail = (e as CustomEvent<{ path?: string; row?: Record<string, unknown>; sent?: string[] }>).detail
  const id = Number(/^\/api\/cleans\/(\d+)$/.exec(detail?.path ?? '')?.[1])
  const row = detail?.row
  const acc = account()
  if (!d || !acc || !row || !Number.isSafeInteger(id)) return
  const had = d.rows.get(id)
  if (!had) return
  if (typeof row.version !== 'number' || row.version < had.version) return
  const gone = row.deleted_at || (typeof row.date === 'string' && row.date !== d.date) ||
    (typeof row.worker === 'string' && row.worker.trim() !== '' && row.worker.trim() !== acc.worker)
  // Given to nobody by this very save (it sent a name, now empty): whose it is depends on the day's roster, so it is
  // off the card until a read says it is still theirs. A save that only carries an unnamed job's name back changes
  // nothing (Astra's final review, rounds 2 and 3).
  if (!gone && (detail.sent ?? []).includes('worker') && (typeof row.worker !== 'string' || row.worker.trim() === '')) {
    d.rows.delete(id)
    d.order = d.order.filter((x) => x !== id)
    d.held.set(id, row.version)
    saveDay(d)
    publishSoon()
    readSoonHook?.()
    return
  }
  if (gone) {
    d.rows.delete(id)
    d.order = d.order.filter((x) => x !== id)
    d.gone.set(id, row.version)
  } else {
    const next = { ...had } as Record<string, unknown>
    for (const f of VISIT_FIELDS) if (f in row) next[f] = row[f]
    d.rows.set(id, next as unknown as TodayClean)
  }
  saveDay(d)
  publishSoon()
}

// A read of the day: which jobs are on it, and every customer word, from the newest read; a visit's own fields only
// when not older than what a saved answer already gave.
let readSeq = 0
let appliedSeq = 0
async function readDay(): Promise<void> {
  const acc = account()
  if (!acc || isLocalOnly() || !(await cardSupported())) return
  const seq = ++readSeq
  const myEpoch = epoch
  const date = todayISO()
  const me = getMe()
  const who = me?.role === 'owner' ? `&worker=${encodeURIComponent(acc.worker)}` : ''
  let data: TodayPayload
  try {
    const res = await fetch(`/api/today?from=${date}&to=${date}${who}`, {
      headers: { 'X-Roundly-Business': String(acc.businessId) },
      credentials: 'same-origin',
      signal: AbortSignal.timeout(15_000),
    })
    if (res.status === 401 || res.status === 403) {
      // Only for the account and card this read was made for (GPT-6.1's review).
      if (myEpoch === epoch && account()?.key === acc.key) {
        refusedFor = acc.key
        try { localStorage.removeItem(DAY_PREFIX + acc.key) } catch { /* the in-memory block still holds */ }
        await clearCard()
      }
      return
    }
    if (!res.ok) return
    data = (await res.json()) as TodayPayload
  } catch { return }
  // Answered for an account, round or epoch that is not this one any more, or after a newer read: not used.
  if (myEpoch !== epoch || account()?.key !== acc.key || seq < appliedSeq || !Array.isArray(data?.cleans)) return
  appliedSeq = seq
  if (refusedFor === acc.key) refusedFor = null
  const old = currentDay()
  const rows = new Map<number, TodayClean>()
  const gone = new Map(old?.gone ?? [])
  const held = new Map(old?.held ?? [])
  for (const r of data.cleans.filter((c) => c.date === date)) {
    const tomb = gone.get(r.id)
    if (tomb !== undefined && tomb >= r.version) continue
    gone.delete(r.id)
    // Given to nobody here: only a read at least as new as that save brings it back.
    const hold = held.get(r.id)
    if (hold !== undefined && r.version < hold) continue
    held.delete(r.id)
    const had = old?.rows.get(r.id)
    if (had && had.version > r.version) {
      const keep = { ...r } as Record<string, unknown>
      for (const f of VISIT_FIELDS) keep[f] = (had as unknown as Record<string, unknown>)[f]
      rows.set(r.id, keep as unknown as TodayClean)
    } else rows.set(r.id, r)
  }
  day = { account: acc.key, date, order: [...rows.keys()], rows, gone, held }
  saveDay(day)
  publishSoon()
}

// One of the person's own jobs today as this page knows it now: the last read with every saved answer since (Today's
// card links read it after the card's taps are taken: Astra's final review, round 2).
// 'gone' when a saved answer took it off the day (it stays off), null when this page does not know it.
export function cardRowNow(id: number): TodayClean | 'gone' | null {
  const d = currentDay()
  if (!d) return null
  if ((d.gone.has(id) || d.held.has(id)) && !d.rows.has(id)) return 'gone'
  return d.rows.get(id) ?? null
}

// ---- the snapshot (rule 1, 3a) ---------------------------------------------------------------------------------------

export interface CardJob {
  id: number; version: number; state: string; started_at: string | null; finished_at: string | null; blocked: boolean
  waiting: 'signal' | 'account' | null; refused: boolean; pendingKey: string | null
  street: string; line: string; job: string
  // "11am" for the Dynamic Island; null with no time.
  time: string | null
  // The customer's map position, for the timer nudges test (ios TimerNudges.swift); left out when there is none.
  lat?: number
  lng?: number
}

const isJob = (q: QueuedMutation, id: number): boolean => q.method === 'PATCH' && q.path === `/api/cleans/${id}`
const isRefused = (q: QueuedMutation): boolean => q.status === 'conflict' || q.status === 'failed'

// "3 Ash Lane, N13": the address's first part and the postcode's first half; the postcode alone; or "A customer".
export function cardStreet(address: string | null | undefined, postcode: string | null | undefined): string {
  const street = address ? splitAccessWarning(address).addr.split(',')[0]!.trim() : ''
  const area = (postcode ?? '').trim().split(/\s+/)[0] ?? ''
  if (street && area) return `${street}, ${area}`
  return street || (postcode ?? '').trim() || 'A customer'
}

export function cardJobs(rows: TodayClean[], queue: readonly QueuedMutation[], several: boolean): CardJob[] {
  const shown = rows.map((row) => {
    const mine = queue.filter((q) => isJob(q, row.id)).slice().sort((a, b) => a.createdAt - b.createdAt)
    const refused = mine.some(isRefused)
    // A job with a refused change shows as the server has it: nothing behind the refusal is laid over (B6).
    const over = refused ? row : layWaiting([row], mine)[0]!
    const live = mine.filter((q) => !isRefused(q))
    const waiting = live.some((q) => q.status === 'queued' || q.status === 'sending') ? 'signal' as const
      : live.some((q) => q.status === 'signed-out' || q.status === 'parked') ? 'account' as const : null
    return { row: over, refused, waiting, pendingKey: refused ? null : live.at(-1)?.key ?? null }
  })
  const byId = new Map(shown.map((s) => [s.row.id, s]))
  const ordered = several ? houseOrder(orderDay(shown.map((s) => s.row))) : orderDay(shown.map((s) => s.row))
  return ordered.map((r) => {
    const s = byId.get(r.id)!
    const job = several && r.job_name?.trim() ? r.job_name.trim() : kindLabel(r.kind)
    const time = visibleTime(r)
    return {
      id: r.id, version: r.version, state: r.state, started_at: r.started_at ?? null, finished_at: r.finished_at ?? null,
      blocked: !!r.blocked_reason, waiting: s.waiting, refused: s.refused, pendingKey: s.pendingKey,
      street: cardStreet(r.address, r.postcode),
      line: [r.customer_name || 'A customer', time ? shownTime(time) : null, job].filter(Boolean).join(' · '),
      job,
      time: time ? shownTime(time) : null,
      ...(typeof r.lat === 'number' && typeof r.lng === 'number' && Number.isFinite(r.lat) && Number.isFinite(r.lng) ? { lat: r.lat, lng: r.lng } : {}),
    }
  })
}

function snapshot(): Record<string, unknown> | null {
  const acc = account()
  const d = currentDay()
  if (!acc || !d) return null
  const recent = Date.now() - 3 * 86_400_000
  const taken = Object.entries(jobOutcomes()).filter(([, o]) => o.card && o.at >= recent).map(([k]) => k)
  return {
    v: 1, epoch, builtAt: Date.now(), date: d.date, timeZone: getMe()?.round?.timeZone || roundConfig().timeZone,
    email: acc.email, businessId: acc.businessId,
    jobs: cardJobs(d.order.map((id) => d.rows.get(id)).filter((r): r is TodayClean => !!r), getQueue(), severalJobsOn()),
    taken,
  }
}

let publishTimer: number | undefined
function publishSoon(): void {
  window.clearTimeout(publishTimer)
  publishTimer = window.setTimeout(() => void publish(), 150)
}
async function publish(): Promise<void> {
  if (!(await cardSupported())) return
  const s = snapshot()
  if (s) await call('card.set', s)
}

// ---- taking the app's taps (rule 3, 3a) --------------------------------------------------------------------------------

let intakeChain: Promise<boolean> = Promise.resolve(true)
// Today waits on this before acting on a card link (Sonnet's and Astra's reviews: a Record payment must see the card's
// Finish first): an intake of its own, after any already running. True once it ran; false when the app did not answer
// in time, and then the link does nothing but open at the job. Outside the app: true at once.
export async function cardTapsTaken(): Promise<boolean> {
  if (!roundlyApp()) return true
  return Promise.race([takeTaps(), new Promise<boolean>((done) => setTimeout(() => done(false), 8000))])
}
// One intake at a time (B4).
// True when every tap the app holds for this account is now in the queue (or there were none); false when the app could
// not be asked, or a tap could not be taken (Astra's final review, round 2).
export function takeTaps(): Promise<boolean> {
  const run = (): Promise<boolean> => runIntake().catch(() => false)
  intakeChain = intakeChain.then(run, run)
  return intakeChain
}

const answered = (a: unknown): boolean => !!a && typeof a === 'object' && (a as { ok?: unknown }).ok === true

async function runIntake(): Promise<boolean> {
  if (!(await cardSupported())) return supported === false  // the app said no card: nothing to take; no answer: not known
  const acc = account()
  if (!acc) return true
  // Taken before, and the app not yet told (its answer was lost): told again (B1).
  const untold = cardUntold()
  if (untold.length && answered(await call('card.taken', { keys: untold }))) cardTold(untold)
  const got = (await call('card.taps', { email: acc.email, businessId: acc.businessId })) as { taps?: unknown } | null
  if (!got || !Array.isArray(got.taps)) return false
  // The account may have changed while the app answered: re-checked inside adoptQueued too.
  if (account()?.key !== acc.key) return false
  if (got.taps.length === 0) return true
  const res = adoptQueued(got.taps as CardTap[])
  if (res.rejected.length) await call('card.rejected', { keys: res.rejected })
  const all = (got.taps as { key?: unknown }[]).filter((t) => typeof t?.key === 'string').map((t) => t.key as string)
  const ok = all.every((k) => res.taken.includes(k) || res.rejected.includes(k))
  if (!res.taken.length) return ok
  // The card is handed its new picture first, so nothing it shows goes back while the app forgets the taps.
  await publish()
  if (answered(await call('card.taken', { keys: res.taken }))) cardTold(res.taken)
  return ok
}

// ---- taking the card off ------------------------------------------------------------------------------------------------

let shownFor: string | null = null
let readSoonHook: (() => void) | null = null
async function clearCard(): Promise<void> {
  if (!(await cardSupported())) return
  const e = nextEpoch()
  day = null
  await call('card.clear', { epoch: e })
}

// Signed out, removed from the round, or a paused round (main.ts, signin.ts): no customer's name stays on the lock
// screen. Outside the app it does nothing.
export async function forgetAppCard(): Promise<void> {
  if (roundlyApp()) await clearCard()
}

// ---- watching ------------------------------------------------------------------------------------------------------------

let watching = false
// Once, after sign-in (main.ts).
export function watchAppCard(): void {
  if (watching || !roundlyApp()) return
  watching = true
  void (async () => {
    if (!(await cardSupported())) return
    setCardInUse(true)
    // The app calls this when a card tap was made while the page is alive (in the background, or on screen).
    ;(window as unknown as { roundlyCard?: { changed: () => void } }).roundlyCard = { changed: () => { void takeTaps() } }
    const check = (): void => {
      const now = account()?.key ?? null
      if (now === shownFor) return
      const had = shownFor
      shownFor = now
      // Another account or round, or nobody with jobs: the old card goes, and a new one starts from this account.
      if (had !== null) void clearCard().then(() => { if (now) void startFor() })
      else if (now) void startFor()
    }
    const startFor = async (): Promise<void> => {
      nextEpoch()
      day = null
      publishSoon()
      await takeTaps()
      await readDay()
    }
    onQueueChange(() => { check(); publishSoon() })
    window.addEventListener('roundly:row-saved', rowSaved)
    let readTimer: number | undefined
    const readSoon = (): void => { window.clearTimeout(readTimer); readTimer = window.setTimeout(() => void readDay(), 2000) }
    readSoonHook = readSoon
    for (const ev of ['dropby:saved', 'dropby:data-changed', 'roundly:queued-landed']) window.addEventListener(ev, readSoon)
    window.addEventListener('online', () => void readDay())
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return
      void takeTaps().then(() => readDay())
    })
    check()
  })()
}
